import { createContext, useCallback, useEffect, useState } from 'react';
import type { User, LoginRequest, RegisterRequest } from '@/types/api';
import { login as loginApi, register as registerApi, getCurrentUser, signOut as signOutApi } from '../api/auth-api';
import { getToken, setToken, removeToken, setStoredUser, getStoredUser } from '@/lib/auth';
import { useDataCache } from '@/store/data-cache-context';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (data: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterRequest) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { clearCache } = useDataCache();

  const loadUser = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Try to get user from storage first
      const storedUser = getStoredUser();
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }

      // Verify with API
      const response = await getCurrentUser();
      if (response.success && response.data) {
        setUser(response.data);
        setStoredUser(JSON.stringify(response.data));
      } else {
        removeToken();
        setUser(null);
      }
    } catch {
      removeToken();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = async (data: LoginRequest) => {
    try {
      const response = await loginApi(data);
      if (response.success && response.data) {
        setToken(response.data.token);
        setUser(response.data.user);
        setStoredUser(JSON.stringify(response.data.user));
        return { success: true };
      }
      return { success: false, error: response.message || 'Login failed' };
    } catch (error) {
      return { success: false, error: 'An error occurred during login' };
    }
  };

  const register = async (data: RegisterRequest) => {
    try {
      const response = await registerApi(data);
      if (response.success) {
        const loginResponse = await loginApi({ email: data.email, password: data.password });
        if (loginResponse.success && loginResponse.data) {
          setToken(loginResponse.data.token);
          setUser(loginResponse.data.user);
          setStoredUser(JSON.stringify(loginResponse.data.user));
        }
        return { success: true };
      }
      return { success: false, error: response.message || 'Registration failed' };
    } catch (error) {
      return { success: false, error: 'An error occurred during registration' };
    }
  };

  const logout = () => {
    // Fire-and-forget server-side sign-out so the backend revokes any
    // outstanding phone pairings for this user. The local clear happens
    // regardless — no reason to block sign-out UX on the network.
    void signOutApi();
    removeToken();
    setUser(null);
    clearCache();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
